// CSR-693 — Shared helper that determines which Power Units / Trailers on
// a policy are covered by Physical Damage. Used by the AMS coverage cards
// (`PhysicalDamageVehicleList`) and intentionally placed in `shared/` so
// future server-side reuse (COI rendering, validation) does not require
// a directory move.
//
// PD-covered rule: a vehicle is covered iff it is not removed, not a
// Non-Owned Attached Trailer pseudo-row, and carries a non-empty /
// non-zero comprehensive or collision deductible. Deductibles are read via
// `hasPdDeductible`, which inspects both the Progressive top-level shape
// (`deductibleComp` / `deductibleCollision`) and the legacy NowCerts
// `vehicleSpecificCoverages` array — same source the COI Description of
// Operations uses, so the card never disagrees with the issued certificate.

import type {
  CoverageStatus,
  PhysicalDamageVehicleSelection,
  PolicyCoverages,
  PolicyVehicle,
} from "./schema";
import { hasPdDeductible, isNonOwnedAttachedTrailer } from "./coi-vehicles";

export type PhysicalDamageStatus = CoverageStatus | "absent";

export interface PhysicalDamageVehicleGroup {
  covered: PolicyVehicle[];
  total: number;
}

export interface PhysicalDamageBreakdown {
  powerUnits: PhysicalDamageVehicleGroup;
  trailers: PhysicalDamageVehicleGroup;
  pdStatus: PhysicalDamageStatus;
}

function pdStatusOf(coverages: PolicyCoverages | null | undefined): PhysicalDamageStatus {
  const block = coverages?.physicalDamage;
  if (!block) return "absent";
  return block.status ?? "active";
}

function isActive(v: PolicyVehicle): boolean {
  return !v.removedAt && !isNonOwnedAttachedTrailer(v);
}

// `selectedVins` switches the group into CSR-1144 "curated" mode: covered is
// the set of active vehicles whose VIN is in the selection (intersected with
// the current roster so a VIN the carrier later removed never shows as a
// phantom covered row). `null`/`undefined` keeps the legacy behaviour of
// inferring coverage from per-vehicle deductibles.
function groupFor(
  list: PolicyVehicle[] | null | undefined,
  pdStatus: PhysicalDamageStatus,
  selectedVins: string[] | null,
): PhysicalDamageVehicleGroup {
  if (!Array.isArray(list) || list.length === 0) {
    return { covered: [], total: 0 };
  }
  const active = list.filter(isActive);
  const total = active.length;
  if (pdStatus === "removed") {
    return { covered: [], total };
  }
  if (selectedVins) {
    const wanted = new Set(selectedVins);
    const covered = active.filter((v) => !!v.vin && wanted.has(v.vin));
    return { covered, total };
  }
  const covered = active.filter(hasPdDeductible);
  return { covered, total };
}

// CSR-1144 — the roster the Physical Damage Add/Edit modal offers for
// selection: every active (non-removed, non-NOA) power unit / trailer,
// regardless of per-vehicle deductible. Deductible inference only drives the
// read-only card fallback; the agent curates from the full active roster.
export function getSelectablePhysicalDamageVehicles(policy: {
  vehicles?: PolicyVehicle[] | null;
  trailers?: PolicyVehicle[] | null;
}): { powerUnits: PolicyVehicle[]; trailers: PolicyVehicle[] } {
  const pick = (list?: PolicyVehicle[] | null) =>
    Array.isArray(list) ? list.filter(isActive) : [];
  return { powerUnits: pick(policy.vehicles), trailers: pick(policy.trailers) };
}

// CSR-1458 — the set of active vehicle/trailer VINs whose Physical Damage
// deductibles must be suppressed on the COI. A VIN is excluded iff a curated
// selection exists (`physicalDamageVehicles` not null) and the VIN is NOT in
// the covered set. When no selection exists the set is empty, so the legacy
// policy-level fallback keeps applying to every vehicle (the GEICO shape).
export function getPhysicalDamageExcludedVins(policy: {
  vehicles?: PolicyVehicle[] | null;
  trailers?: PolicyVehicle[] | null;
  coverages?: PolicyCoverages | null;
  physicalDamageVehicles?: PhysicalDamageVehicleSelection | null;
}): Set<string> {
  const excluded = new Set<string>();
  if (!policy.physicalDamageVehicles) return excluded;

  const breakdown = getPhysicalDamageVehicles(policy);
  const coveredVins = new Set<string>();
  for (const v of breakdown.powerUnits.covered) if (v.vin) coveredVins.add(v.vin);
  for (const v of breakdown.trailers.covered) if (v.vin) coveredVins.add(v.vin);

  const collectExcluded = (list?: PolicyVehicle[] | null) => {
    if (!Array.isArray(list)) return;
    for (const v of list.filter(isActive)) {
      if (v.vin && !coveredVins.has(v.vin)) excluded.add(v.vin);
    }
  };
  collectExcluded(policy.vehicles);
  collectExcluded(policy.trailers);
  return excluded;
}

export function getPhysicalDamageVehicles(policy: {
  vehicles?: PolicyVehicle[] | null;
  trailers?: PolicyVehicle[] | null;
  coverages?: PolicyCoverages | null;
  physicalDamageVehicles?: PhysicalDamageVehicleSelection | null;
}): PhysicalDamageBreakdown {
  const pdStatus = pdStatusOf(policy.coverages);
  const selection = policy.physicalDamageVehicles ?? null;
  return {
    powerUnits: groupFor(policy.vehicles, pdStatus, selection ? selection.vehicleVins : null),
    trailers: groupFor(policy.trailers, pdStatus, selection ? selection.trailerVins : null),
    pdStatus,
  };
}
